import { pick, extend, chain, contains, pluck, isString, isArray, findWhere, isEmpty, reduce, filter, find } from 'underscore';
import { readCampaign } from '@bingads-webui-campaign-react/perf-max-data';
import { convertToMTFormat, Scopes } from '@bingads-webui-campaign-react/goal-selector';
import {
  modelToViewModelForLocationTargets,
  modelToViewModelForDayTimeTargets,
  bulkModelToViewModelForLocationTargets,
  bulkModelToViewModelForDayTimeTargets,
  modelToViewModelForCampaignNegativeWebpages,
  bulkModelToViewModelForCampaignNegativeWebpages
} from './campaign-criterions';
import { initialCampaignData, CampaignNegativeReferenceId, initialBrandExclusionsData } from '../common/constants';

export const initialCampaignViewModel = {
  data: initialCampaignData,
  isValid: false,
  isDirty: false,
};

export const initialBrandExclusionsViewModel = {
  data: initialBrandExclusionsData,
  isDirty: false,
};

const whitelistedSchemaPropertyKeysForModel = [
  'Notes', 'Budget', 'Id', 'Name', 'TimeZone', 'BidStrategyName', 'Status',
  'DeliveryStatus', 'DeliveryStatusDetails', 'CampaignType', 'CampaignSubType',
  'HasAuctionInsight', 'IsBidLandscapeAvailable', 'TrackingUrlTemplate',
  'FinalUrlSuffix', 'UrlCustomParameters', 'BiddingScheme', 'NativeBidAdjustment',
  'CampaignSettings', 'DisplayBiddingStrategyType',
  'DisplayBiddingStrategyMaxCpc', 'DisplayBiddingStrategyTargetCpa', 'Languages',
  'ExperimentId', 'BaseCampaignName', 'TargetSetting',
  'AdScheduleUseSearcherTimeZone', 'AvailableInsightTypeIds',
  'MultiMediaAdBidAdjustment', 'DisclaimerAdsEnabled', 'IsBSREnabled', 'BSRAdDistribution',
  // 'CampaignCashback', 'KeywordVariantMatchEnabled',
];

const whitelistedSchemaPropertyKeys = whitelistedSchemaPropertyKeysForModel.concat(['CampaignConversionGoal']);

export const modelToViewModelForConversionGoals = (campaignModel) => {
  const hasCampaignLevelGoals = campaignModel.CampaignConversionGoal && campaignModel.CampaignConversionGoal.Goals
    && campaignModel.CampaignConversionGoal.Goals.length > 0;
  return {
    conversionGoals: {
      originalConversionGoals: campaignModel.CampaignConversionGoal.Goals,
      goalSelection: campaignModel.CampaignConversionGoal.Goals,
      goalScopeSelection: hasCampaignLevelGoals ?
        Scopes.CAMPAIGN : Scopes.ACCOUNT,
    },
  };
};

export const modelToViewModelForNewCustomerAcquisitionGoals = ({ accountSettings, campaignSettings }) => {
  const campaignSettingsModel = {
    goalId: null,
    bidOnlyMode: false,
    additionalConversionValue: null,
  };
  if (isArray(campaignSettings)) {
    const campaignGoal = findWhere(campaignSettings, { '@odata.type': '#Model.NewCustomerAcquisitionGoalSetting' });
    if (campaignGoal) {
      campaignSettingsModel.goalId = campaignGoal.NewCustomerAcquisitionGoalId;
      campaignSettingsModel.bidOnlyMode = campaignGoal.NewCustomerAcquisitionBidOnlyMode;
      campaignSettingsModel.additionalConversionValue = campaignGoal.AdditionalConversionValue;
    }
  }
  return {
    newCustomerAcquisitionOptOut: accountSettings === null || campaignSettingsModel.goalId === null || campaignSettingsModel.goalId === 'delete_value',
    newCustomerAcquisition: {
      campaignSettings: campaignSettingsModel,
      accountSettings: isEmpty(accountSettings)
        ? null
        : {
          goalId: accountSettings[0].Id,
          additionalConversionValue: accountSettings[0].AdditionalValue,
          audiences: accountSettings[0].Audiences,
        },
    },
  };
};

export const modelToViewModelForCampaign = ({
  campaignModel,
  locationCriterionsModel,
  dayTimeCriterionsModel,
  campaignNegativeWebpagesModel,
  bundleCountries,
  newCustomerAcquisitionGoalsModel,
}) => {
  let data = pick(campaignModel, [...whitelistedSchemaPropertyKeys, 'ImportProperties']);
  if (locationCriterionsModel) {
    data = extend(data, modelToViewModelForLocationTargets({
      model: locationCriterionsModel,
      bundleCountries,
      campaignId: campaignModel.Id,
    }));
  }

  if (dayTimeCriterionsModel) {
    data = extend(data, modelToViewModelForDayTimeTargets({
      model: dayTimeCriterionsModel,
    }));
  }

  if (campaignNegativeWebpagesModel) {
    data = extend(data, modelToViewModelForCampaignNegativeWebpages({
      model: campaignNegativeWebpagesModel,
    }));
  }

  if (newCustomerAcquisitionGoalsModel) {
    data = extend(data, modelToViewModelForNewCustomerAcquisitionGoals({
      accountSettings: newCustomerAcquisitionGoalsModel,
      campaignSettings: campaignModel.CampaignSettings,
    }));
  }

  if (campaignModel.CampaignSettings && campaignModel.CampaignSettings[0] && campaignModel.CampaignSettings[0]['@odata.type'] === '#Model.ShoppingSettings') {
    data.ProviderId = campaignModel.CampaignSettings[0].ProviderId;
    data.SalesCountry = campaignModel.CampaignSettings[0].SalesCountry;
    data.FeedLabel = campaignModel.CampaignSettings[0].FeedLabel;
    data.IsFeedFiltered = campaignModel.CampaignSettings[0].SalesCountry || campaignModel.CampaignSettings[0].FeedLabel;
    data.LocalInventoryAdsEnabled = campaignModel.CampaignSettings[0].LocalInventoryAdsEnabled;
  }

  // if (campaignModel.BiddingScheme) {
  //   data.BiddingScheme.ManualCpc = campaignModel.BiddingScheme.ManualCPC;
  // }

  if (isArray(campaignModel.CampaignSettings)) {
    const perfMaxCampaignSetting = findWhere(campaignModel.CampaignSettings, { '@odata.type': '#Model.PerformanceMaxSetting' });
    if (perfMaxCampaignSetting) {
      data.finalUrlExpansionOptOut = perfMaxCampaignSetting.FinalUrlExpansionOptOut;
      data.autoGeneratedTextOptOut = perfMaxCampaignSetting.AutoGeneratedTextOptOut;
      data.autoGeneratedImageOptOut = perfMaxCampaignSetting.AutoGeneratedImageOptOut;
      data.costPerSaleOptOut = perfMaxCampaignSetting.CostPerSaleOptOut;
    }
  }

  if (isArray(campaignModel.CampaignSettings)) {
    const perfMaxSelectCampaignSetting = findWhere(campaignModel.CampaignSettings, { '@odata.type': '#Model.PerformanceMaxSelectSetting' });
    if (perfMaxSelectCampaignSetting) {
      data.IsBSREnabled = perfMaxSelectCampaignSetting.IsBSREnabled;
      data.BSRAdDistribution = perfMaxSelectCampaignSetting.BSRAdDistribution;
    }
  }

  data = extend(data, modelToViewModelForConversionGoals(campaignModel));

  if (campaignModel.Status === 'UserPaused') {
    data.Status = 'Paused'; // if we send back userpaused mt treats it as active, we explicitly need to send paused
  }

  const campaignViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return campaignViewModel;
};

export const bulkModelToViewModelForConversionGoals = ({
  campaignModel,
  originalCampaignViewModel,
  errors = [],
}) => {
  const conversionGoalsBulkModel = campaignModel.CampaignConversionGoalAssociations;
  const errorKey = 'Client Id';
  const originalGoalSelection =
    chain(conversionGoalsBulkModel)
      .filter(goal => !contains(pluck(errors, errorKey), goal[errorKey]))
      .filter(goal => goal.Action === 'Add')
      .map(goal => ({
        Id: goal.GoalId,
      }))
      .value();
  return {
    conversionGoals: {
      ...originalCampaignViewModel.data.conversionGoals,
      originalConversionGoals: originalGoalSelection,
    },
  };
};

const and = (predicate1, predicate2) => item => predicate1(item) && predicate2(item);
const or = (predicate1, predicate2) => item => predicate1(item) || predicate2(item);
const not = predicate => item => !predicate(item);
const errorIs = errorCode => error => error.Error === errorCode;
const clientIdIs = clientId => error => error['Client Id'] === clientId;

const extractAndRestoreFailedBrandListAssociations = (errors, BrandListsAssociations) => {
  const { savedAddedBrandListIds, savedDeletedBrandListIds, filteredErrors } =
    reduce(BrandListsAssociations, (acc, brandListAssociation) => {
      const brandListAssociationClientId = clientIdIs(brandListAssociation['Client Id']);

      const brandListAssociationErrors = filter(acc.filteredErrors, brandListAssociationClientId);
      if (isEmpty(brandListAssociationErrors)) {
        if (brandListAssociation.Status === 'Active') {
          acc.savedAddedBrandListIds.push(brandListAssociation.SharedEntityId);
        } else if (brandListAssociation.Status === 'Deleted') {
          acc.savedDeletedBrandListIds.push(brandListAssociation.SharedEntityId);
        }
      } else {
        const campaignServiceSharedEntityAssociationDoesNotExistError = errorIs('CampaignServiceSharedEntityAssociationDoesNotExist');

        // Some errors are non-blocking and should be filtered out
        // Case #1.1: Deleting an association that doesn't exist
        // Case #1.2: Deleting an association where both the association and the brand list are already deleted
        const associationDoesNotExistError = find(brandListAssociationErrors, campaignServiceSharedEntityAssociationDoesNotExistError);
        if (associationDoesNotExistError) {
          acc.filteredErrors = acc.filteredErrors.filter(not(or(
            and(campaignServiceSharedEntityAssociationDoesNotExistError, brandListAssociationClientId),
            and(errorIs('CampaignServiceSharedListIdInvalid'), brandListAssociationClientId)
          )));
        }

        // Case #2: Adding an association that already exists
        acc.filteredErrors = acc.filteredErrors
          .filter(not(and(errorIs('CampaignServiceSharedEntityAssociationDuplicate'), brandListAssociationClientId)));
      }
      return acc;
    }, { savedAddedBrandListIds: [], savedDeletedBrandListIds: [], filteredErrors: errors });

  return {
    savedAddedBrandListIds, savedDeletedBrandListIds, filteredErrors,
  };
};

export const bulkModelToViewModelForBrandExclusions = ({ errors = [], campaignModel }) => {
  const { BrandListsAssociations = [] } = campaignModel;
  const {
    savedAddedBrandListIds, savedDeletedBrandListIds, filteredErrors,
  } = extractAndRestoreFailedBrandListAssociations(errors, BrandListsAssociations);

  return {
    brandExclusions: {
      data: {
        savedAddedBrandListIds,
        savedDeletedBrandListIds,
      },
      isDirty: false,
    },
    errors: filteredErrors,
  };
};

const bulkModelToViewModelForNewCustomerAcquisitionGoals = ({ campaignModel, originalCampaignViewModel }) => {
  if (!originalCampaignViewModel.data.newCustomerAcquisition) return {};
  return {
    newCustomerAcquisition: {
      accountSettings: originalCampaignViewModel.data.newCustomerAcquisition.accountSettings,
      campaignSettings: {
        goalId: campaignModel.NewCustomerAcquisitionGoalId,
        bidOnlyMode: campaignModel.NewCustomerAcquisitionBidOnlyMode,
        additionalConversionValue: campaignModel.AdditionalConversionValue === 'delete_value' ? null : campaignModel.AdditionalConversionValue,
      },
    },
  };
};

export const bulkModelToViewModelForCampaign = ({
  campaignModel,
  originalCampaignViewModel,
  bundleCountries,
  errors,
}) => {
  let data = pick(campaignModel, whitelistedSchemaPropertyKeys);
  if (data.Id && isString(data.Id)) {
    // eslint-disable-next-line no-param-reassign
    data.Id = parseInt(data.Id, 10);
  }
  data = extend(data, bulkModelToViewModelForLocationTargets({
    locationTargetsModel: campaignModel.CampaignCriterions,
    bundleCountries,
  }));
  data = extend(data, bulkModelToViewModelForConversionGoals({
    campaignModel,
    originalCampaignViewModel,
    errors,
  }));
  data = extend(data, bulkModelToViewModelForDayTimeTargets({
    criterions: campaignModel.CampaignCriterions,
  }));
  data = extend(data, bulkModelToViewModelForCampaignNegativeWebpages({
    model: campaignModel.CampaignNegativeWebpages,
  }));

  data = extend(data, bulkModelToViewModelForNewCustomerAcquisitionGoals({
    campaignModel,
    originalCampaignViewModel,
  }));

  data = extend(data, {
    ProviderId: originalCampaignViewModel.data.ProviderId,
    SalesCountry: originalCampaignViewModel.data.SalesCountry,
    FeedLabel: originalCampaignViewModel.data.FeedLabel,
    LocalInventoryAdsEnabled: originalCampaignViewModel.data.LocalInventoryAdsEnabled,
    finalUrlExpansionOptOut: originalCampaignViewModel.data.finalUrlExpansionOptOut,
    autoGeneratedImageOptOut: originalCampaignViewModel.data.autoGeneratedImageOptOut,
    autoGeneratedTextOptOut: originalCampaignViewModel.data.autoGeneratedTextOptOut,
    costPerSaleOptOut: originalCampaignViewModel.data.costPerSaleOptOut,
    IsBSREnabled: originalCampaignViewModel.data.IsBSREnabled,
    BSRAdDistribution: originalCampaignViewModel.data.BSRAdDistribution,
  });

  data = extend(data, {
    pageFeeds: originalCampaignViewModel.data.pageFeeds,
  });

  const campaignViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };
  return campaignViewModel;
};

const viewModelToModelForUrlCustomParameters = urlCustomParametersViewModel =>
  urlCustomParametersViewModel.filter(({ Key }) => Key);

export const viewModelToBulkModelForNewCustomerAcquisitionGoals = (campaignViewModel) => {
  const { newCustomerAcquisition, newCustomerAcquisitionOptOut } = campaignViewModel.data;
  if (!newCustomerAcquisition) return null;

  const { accountSettings, campaignSettings } = newCustomerAcquisition;

  if (!accountSettings) return {};

  return {
    NewCustomerAcquisitionGoalId: newCustomerAcquisitionOptOut ? 'delete_value' : accountSettings.goalId,
    NewCustomerAcquisitionBidOnlyMode: campaignSettings.bidOnlyMode,
    AdditionalConversionValue: campaignSettings.bidOnlyMode || campaignSettings.additionalConversionValue === null ? 'delete_value' : campaignSettings.additionalConversionValue,
  };
};

export const viewModelToBulkModelForCampaign = (campaignViewModel, brandExclusionsViewModel) => {
  const model = pick(campaignViewModel.data, whitelistedSchemaPropertyKeysForModel);

  if (model.UrlCustomParameters) {
    // only convert if we have UrlCustomParameters.
    model.UrlCustomParameters = viewModelToModelForUrlCustomParameters(model.UrlCustomParameters);
  }

  if (campaignViewModel.data.ProviderId) {
    model.ProviderId = campaignViewModel.data.ProviderId;
    model.Priority = 'Low';
    if (campaignViewModel.data.FeedLabel) {
      model.FeedLabel = campaignViewModel.data.FeedLabel;
    } else if (campaignViewModel.data.SalesCountry) {
      model.SalesCountry = campaignViewModel.data.SalesCountry;
    }
    const isEditMode = campaignViewModel.data.Id && campaignViewModel.data.Id > 0;
    if (isEditMode) {
      if (!campaignViewModel.data.FeedLabel) {
        model.FeedLabel = 'delete_value';
      }
      if (!campaignViewModel.data.SalesCountry) {
        model.SalesCountry = 'delete_value';
      }
    }

    model.LocalInventoryAdsEnabled = campaignViewModel.data.LocalInventoryAdsEnabled;
  }

  model.AutoGeneratedImageOptOut = campaignViewModel.data.autoGeneratedImageOptOut;
  model.AutoGeneratedTextOptOut = campaignViewModel.data.autoGeneratedTextOptOut;
  model.FinalUrlExpansionOptOut = campaignViewModel.data.finalUrlExpansionOptOut;

  model.CostPerSaleOptOut = campaignViewModel.data.costPerSaleOptOut;

  if (isArray(campaignViewModel.data.pageFeeds)) {
    model.PageFeedIds = campaignViewModel.data.pageFeeds.map(pageFeed => pageFeed.key);
  }

  if (isArray(brandExclusionsViewModel.data.deletedBrandListIds) && !isEmpty(brandExclusionsViewModel.data.deletedBrandListIds)) {
    model.BrandListsAssociations = [
      ...model.BrandListsAssociations || [],
      ...brandExclusionsViewModel.data.deletedBrandListIds.map(brandListId => ({
        Id: brandListId, SharedEntityId: brandListId, EntityId: campaignViewModel.data.Id || CampaignNegativeReferenceId, Status: 'Deleted',
      })),
    ];
  }
  if (isArray(brandExclusionsViewModel.data.addedBrandListIds) && !isEmpty(brandExclusionsViewModel.data.addedBrandListIds)) {
    model.BrandListsAssociations = [
      ...model.BrandListsAssociations || [],
      ...brandExclusionsViewModel.data.addedBrandListIds.map(brandListId => ({
        Id: brandListId, SharedEntityId: brandListId, EntityId: campaignViewModel.data.Id || CampaignNegativeReferenceId, Status: 'Active',
      })),
    ];
  }

  if (!campaignViewModel.data.Id) {
    model.Id = CampaignNegativeReferenceId;
  }
  return model;
};

export const viewModelToModelForCampaign = (campaignViewModel) => {
  const model = pick(campaignViewModel.data, whitelistedSchemaPropertyKeysForModel);
  if (campaignViewModel.data.ProviderId) {
    model.CampaignSettings = {
      ProviderId: campaignViewModel.data.ProviderId,
      LocalInventoryAdsEnabled: campaignViewModel.data.LocalInventoryAdsEnabled,
      SalesCountry: campaignViewModel.data.SalesCountry,
      FeedLabel: campaignViewModel.data.FeedLabel,
    };
  }
  return model;
};

export const viewModelToBulkModelForConversionGoals = (campaignViewModel, campaignId) => {
  const { conversionGoals } = campaignViewModel.data;
  return convertToMTFormat(
    campaignId,
    conversionGoals.goalScopeSelection,
    conversionGoals.originalConversionGoals,
    conversionGoals.goalSelection,
    true
  );
};

export const loadCampaign = (cid, aid, campaignId) =>
  readCampaign(cid, aid, campaignId);

export const loadCampaignAsViewModel = (cid, aid, campaignId) =>
  loadCampaign(cid, aid, campaignId)
    .then(campaignModel => modelToViewModelForCampaign({
      campaignModel,
    }));
